const WIDGET_ORIGIN = 'https://services.gbmembers.net'
const LOADER_TIMEOUT_MS = 10000
/** Wait after window.load before warming the iframe (lets hero/CSS settle). */
const POST_LOAD_DELAY_MS = 2000
/** Idle timeout cap so the widget still warms even on busy main threads. */
const IDLE_TIMEOUT_MS = 4000
/** Desktop min height — widget often reports ~340px for the first view. */
const DESKTOP_MIN_HEIGHT = 500
const MOBILE_MIN_HEIGHT = 320
const DESKTOP_MQ = '(min-width: 992px)'

let iframe = null
let loader = null
let iframeSrc = ''
let loaderTimeoutId = null
let widgetReady = false
let listenersBound = false
let loadScheduled = false

/**
 * Toggle booking loader visibility.
 * @param {boolean} isLoading
 */
function setLoading (isLoading) {
  if (!loader) return

  loader.dataset.show = isLoading ? 'on' : 'off'
  loader.setAttribute('aria-hidden', String(!isLoading))
}

function clearLoaderTimeout () {
  if (loaderTimeoutId === null) return

  window.clearTimeout(loaderTimeoutId)
  loaderTimeoutId = null
}

/**
 * Hide loader once the widget is usable (or timed out).
 */
function markWidgetReady () {
  if (widgetReady) return

  widgetReady = true
  clearLoaderTimeout()
  setLoading(false)
}

/**
 * Minimum iframe height by viewport (desktop starts taller than the widget's first report).
 * @returns {number}
 */
function getMinIframeHeight () {
  return window.matchMedia(DESKTOP_MQ).matches
    ? DESKTOP_MIN_HEIGHT
    : MOBILE_MIN_HEIGHT
}

/**
 * Expand iframe to the widget's full content height. Never cap to the modal —
 * .booking-modal is the only scroll container.
 * @param {number} height
 */
function applyIframeHeight (height) {
  if (!iframe) return

  const nextHeight = Math.max(getMinIframeHeight(), Math.ceil(height))
  iframe.style.height = `${nextHeight}px`
  iframe.setAttribute('scrolling', 'no')
  iframe.style.overflow = 'hidden'
}

/**
 * Resolve DOM refs once (safe to call before/after init).
 */
function bindBookingElements () {
  if (iframe) return Boolean(iframeSrc)

  iframe = document.getElementById('gbbookatrial')
  if (!iframe) return false

  loader = iframe.closest('.booking-widget-container')?.querySelector('.booking-loader')
  iframeSrc = iframe.dataset.src || ''

  return Boolean(iframeSrc)
}

/**
 * Skip background warm on constrained networks.
 * @returns {boolean}
 */
function shouldDeferWarm () {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection

  if (!connection) return false
  if (connection.saveData) return true

  const type = connection.effectiveType
  return type === 'slow-2g' || type === '2g'
}

/**
 * Load the booking iframe (idempotent). Used on open, hover, and post-load warm.
 */
export function ensureBookingIframe () {
  if (!bindBookingElements()) return

  if (iframe.getAttribute('src')) {
    if (widgetReady) setLoading(false)
    return
  }

  widgetReady = false
  setLoading(true)
  clearLoaderTimeout()
  loaderTimeoutId = window.setTimeout(markWidgetReady, LOADER_TIMEOUT_MS)
  iframe.setAttribute('src', iframeSrc)
}

/**
 * Warm iframe in the background after the page has fully loaded.
 */
function schedulePostLoadWarm () {
  if (loadScheduled) return
  loadScheduled = true

  const startWarm = () => {
    if (shouldDeferWarm()) return
    if (iframe?.getAttribute('src')) return

    const run = () => ensureBookingIframe()

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: IDLE_TIMEOUT_MS })
    } else {
      window.setTimeout(run, 0)
    }
  }

  const afterLoad = () => {
    window.setTimeout(startWarm, POST_LOAD_DELAY_MS)
  }

  if (document.readyState === 'complete') {
    afterLoad()
  } else {
    window.addEventListener('load', afterLoad, { once: true })
  }
}

/**
 * Start loading early if the user shows intent (hover / focus on CTA).
 */
function bindIntentWarm () {
  const triggers = document.querySelectorAll('.open-modal[data-modal="booking"]')

  const warm = () => ensureBookingIframe()

  triggers.forEach((el) => {
    el.addEventListener('pointerenter', warm, { once: true, passive: true })
    el.addEventListener('focus', warm, { once: true, passive: true })
  })
}

/**
 * Init booking widget: listeners + deferred warm after full page load.
 */
function bookingWidget () {
  if (!bindBookingElements()) return
  if (iframe.dataset.widgetInitialized === 'true') return

  iframe.dataset.widgetInitialized = 'true'
  setLoading(true)
  iframe.addEventListener('error', () => markWidgetReady())

  if (!listenersBound) {
    listenersBound = true

    window.addEventListener('message', (event) => {
      if (event.origin !== WIDGET_ORIGIN || event.source !== iframe?.contentWindow) return
      if (!event.data || typeof event.data !== 'object') return

      if (event.data.action === 'gbMembersAdjustIFrame') {
        const height = Number(event.data.height)

        if (!Number.isFinite(height) || height <= 0) return

        applyIframeHeight(height)
        markWidgetReady()
      }

      if (event.data.action === 'gbLoadCustomThankyou') {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'trial_booking_success',
          location: 'stpetersburg'
        })

        if (typeof event.data.url !== 'string' || event.data.url.trim() === '') return

        let redirectUrl

        try {
          redirectUrl = new URL(event.data.url, WIDGET_ORIGIN)
        } catch {
          return
        }

        if (!['http:', 'https:'].includes(redirectUrl.protocol)) return

        window.location.assign(redirectUrl.toString())
      }
    })
  }

  bindIntentWarm()
  schedulePostLoadWarm()
}

export default bookingWidget
